import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, Shield, Award } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../services/api';

const WelcomePage = () => {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await api.get('/content/welcome_page');
      setContent(response.data.content_value);
    } catch (error) {
      console.error('Failed to load welcome content');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    navigate('/admin/onboarding/step2');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden"
        >
          {/* Header with WAVE branding */}
          <div className="bg-gradient-to-r from-emerald-900 to-emerald-800 p-8 text-white">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Award className="w-10 h-10 text-white" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-center mb-2">Welcome to Trados!</h1>
            <p className="text-center text-emerald-100 text-lg">Your Gateway to Brazilian Commodities</p>
          </div>

          {/* Content */}
          <div className="p-10">
            <div 
              data-testid="welcome-content"
              className="prose prose-slate max-w-none mb-8 text-slate-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: content }}
            />

            {/* Call to action */}
            <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="bg-amber-600 rounded-full p-3 flex-shrink-0">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-slate-800 text-lg mb-2">
                    First Step: Get Approved as a Trados Customer
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Before you can submit LOI requests, we need to verify your identity and business credentials. This ensures a secure and trustworthy trading environment for all parties.
                  </p>
                  <p className="text-sm text-slate-500">
                    <strong>Note:</strong> This is a necessary step to comply with international trade regulations and protect all parties involved.
                  </p>
                </div>
              </div>
            </div>

            {/* Next button */}
            <button
              onClick={handleNext}
              data-testid="welcome-next-button"
              className="w-full bg-emerald-900 hover:bg-emerald-800 text-white py-5 px-8 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 group"
            >
              <span>Click Here to Begin Verification</span>
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </button>

            {user?.role === 'admin' && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => navigate('/admin/content')}
                  className="text-sm text-emerald-900 hover:text-emerald-700 underline"
                >
                  Edit Welcome Page Content (Admin)
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WelcomePage;
